import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { NavigationEnd, Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { CommonUtilityService } from '../../utility/common-utility.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MaterialModulesModule } from '../../material-modules/material-modules.module';

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrl: './notifications.component.scss',
    standalone: true,
    imports: [
      CommonModule,
      MaterialModulesModule
    ]
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @ViewChild('notificationMenuTrigger') notificationMenuTrigger!: MatMenuTrigger;
  
  notifications: any[] = [];
  isLoading = false;
  unreadCount = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private commonService: CommonUtilityService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadNotifications();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.loadNotifications());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadNotifications(): void {
    this.isLoading = true;
    this.notificationService.getBrokerNotifications().subscribe({
      next: (response: any) => {
        this.notifications = Array.isArray(response) ? response : [];
        this.updateUnreadCount();
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading notifications:', error);
        this.isLoading = false;
        this.commonService.showSnackbar('Failed to load notifications');
      }
    });
  }

  updateUnreadCount(): void {
    this.unreadCount = this.notifications.filter(n => !n.isRead).length;
  }

  toggleMarkAsRead(notification: any, event: Event): void {
    event.stopPropagation();
    if (notification.isRead) {
      this.notificationService.markAsUnRead({ ids: [notification.id] }).subscribe({
        next: () => {
          notification.isRead = false;
          this.updateUnreadCount();
        },
        error: (error: any) => {
          console.error('Error marking notification as unread:', error);
          this.commonService.showSnackbar('Failed to mark notification as unread');
        }
      });
    } else {
      this.notificationService.markAsRead({ ids: [notification.id] }).subscribe({
        next: () => {
          notification.isRead = true;
          this.updateUnreadCount(); 
        },
        error: (error: any) => {
          console.error('Error marking notification as read:', error);
          this.commonService.showSnackbar('Failed to mark notification as read');
        }
      });
    }
  }

  markAllAsRead(event?: Event): void {
    event.stopPropagation();
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.notificationService.markAsRead({ ids: [], all: true }).subscribe({
      next: () => {
        this.notifications.forEach(n => n.isRead = true);
        this.updateUnreadCount();
        this.commonService.showSnackbar('All notifications marked as read', 'simple');
        // Keep notification menu open
        setTimeout(() => {
          if (this.notificationMenuTrigger && !this.notificationMenuTrigger.menuOpen) {
            this.notificationMenuTrigger.openMenu();
          }
        }, 100);
      },
      error: (error: any) => {
        console.error('Error marking all as read:', error);
        this.commonService.showSnackbar('Failed to mark all notifications as read');
      }
    });
  }

  markAsArchived(notification: any, event: Event): void {
    event.stopPropagation();

    this.notificationService.archiveAlert({ ids: [notification.id] }).subscribe({
      next: () => {
        this.loadNotifications();
      },
      error: (error: any) => {
        console.error('Error archiving notification:', error);
        this.commonService.showSnackbar('Failed to archive notification');
      }
    });
  }

  markAllAsArchived(event?: Event): void {
    event.stopPropagation();
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.notificationService.archiveAlert({ ids: this.notifications.map(n => n.id) }).subscribe({
      next: () => {
        this.loadNotifications();
        this.commonService.showSnackbar('All notifications archived', 'simple');
        // Keep notification menu open
        setTimeout(() => {
          if (this.notificationMenuTrigger && !this.notificationMenuTrigger.menuOpen) {
            this.notificationMenuTrigger.openMenu();
          }
        }, 100);
      },
      error: (error: any) => {
        console.error('Error archiving all notifications:', error);
        this.commonService.showSnackbar('Failed to archive all notifications');
      }
    });
  }

  markAllReadAsArchived(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    let ids = this.notifications.filter((obj)=> {
      return obj.isRead;
    }).map((obj)=>{
      return obj.id
    });

    this.notificationService.archiveAlert({ids: ids}).subscribe({
      next: () => {
        this.loadNotifications();
        this.commonService.showSnackbar('All read notifications archived', 'simple');
        // Keep notification menu open
        setTimeout(() => {
          if (this.notificationMenuTrigger && !this.notificationMenuTrigger.menuOpen) {
            this.notificationMenuTrigger.openMenu();
          }
        }, 100);
      },
      error: (error: any) => {
        console.error('Error archiving read notifications:', error);
        this.commonService.showSnackbar('Failed to archive read notifications');
      }
    });
  }

  onOptionsButtonClick(event: Event): void {
    event.stopPropagation();
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInHours / 24;

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`;
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  onMenuOpened(): void {
  }

  onMenuClosed(): void {
    // This is called when menu closes, but we can't prevent it here
    // The menu will close naturally when clicking outside or on backdrop
  }

  closeNotificationMenu(event: Event): void {
    event.stopPropagation();
    if (this.notificationMenuTrigger) {
      this.notificationMenuTrigger.closeMenu();
    }
  }
}

